const get = require('lodash/get')
const keyBy = require('lodash/keyBy')
const XLSX = require('@sheet/core')
const format = require('date-fns/format')

const formatDate = (date) => {
  if (!date) return ''
  return format(new Date(date), 'YYYY-MM-DD')
}

const exportLongHaulOrdersData = async (state, { api, orders }) => {
  // Fetch all transfer orders from CouchDB in parallel (one group per supply plan)
  const groups = await Promise.all(
    orders.map(order =>
      api.order.getGroup({ groupId: order.order_group_id, useAcceptedStatus: true })
        .then(group => ({
          supplyPlan: order,
          transferOrders: (group.orders || []).filter(o => o.orderType === 'interwarehouse')
        }))
        .catch(() => ({ supplyPlan: order, transferOrders: [] }))
    )
  )

  // Pair each transfer order with its supply plan, filtering out empty ones
  const transferOrderEntries = []
  groups.forEach(({ supplyPlan, transferOrders }) => {
    transferOrders.forEach(transferOrder => {
      const hasProducts = Object.values(transferOrder.products || {}).some(p => (p.adjusted || 0) !== 0)
      if (hasProducts) {
        transferOrderEntries.push({ supplyPlan, transferOrder })
      }
    })
  })

  // Collect unique location IDs and product IDs across all transfer orders
  const locationIds = new Set()
  const productIds = new Set()
  transferOrderEntries.forEach(({ transferOrder }) => {
    if (transferOrder.supplierId) locationIds.add(transferOrder.supplierId)
    if (transferOrder.destinationId) locationIds.add(transferOrder.destinationId)
    Object.keys(transferOrder.products || {}).forEach(id => productIds.add(id))
  })

  const [locations, products] = await Promise.all([
    locationIds.size ? api.location.getByIds([...locationIds]) : Promise.resolve([]),
    productIds.size ? api.product.getByIds([...productIds]) : Promise.resolve([])
  ])

  const locationsById = keyBy(locations, '_id')
  const productsById = keyBy(products, '_id')

  // Build one row per product per transfer order
  const rows = []
  transferOrderEntries.forEach(({ supplyPlan, transferOrder }) => {
    const supplierLocation = locationsById[transferOrder.supplierId]
    const destinationLocation = locationsById[transferOrder.destinationId]

    Object.entries(transferOrder.products || {}).forEach(([productId, productData]) => {
      const quantityOrdered = productData.adjusted || 0
      if (quantityOrdered === 0) return

      const product = productsById[productId]
      rows.push({
        orderId: transferOrder.groupId || '',
        shipmentNo: transferOrder.suborderId || '',
        originatingWarehouseCode: get(supplierLocation, 'additionalData.warehouseCode', ''),
        originatingWarehouseName: get(supplierLocation, 'name', ''),
        destinationWarehouseCode: get(destinationLocation, 'additionalData.warehouseCode', ''),
        destinationWarehouseName: get(destinationLocation, 'name', ''),
        genericProductCode: get(product, 'genericParent', ''),
        productId: get(product, '_id', ''),
        productName: get(product, 'name', ''),
        productCode: get(product, 'code', ''),
        quantityOrdered,
        unitVolume: Number(get(product, 'unitVolume', 0)) || 0,
        unitPrice: Number(get(product, 'unitPrice', 0)) || 0,
        funderId: transferOrder.funderId || '',
        programId: transferOrder.programId || '',
        status: supplyPlan.status || '',
        createdAt: formatDate(supplyPlan.created_at || transferOrder.createdAt)
      })
    })
  })

  const columns = [
    { name: 'Order ID', key: 'orderId' },
    { name: 'Route', key: null },
    { name: 'Shipment No', key: 'shipmentNo' },
    { name: 'Originating Warehouse Code', key: 'originatingWarehouseCode' },
    { name: 'Originating Warehouse Name', key: 'originatingWarehouseName' },
    { name: 'Destination Warehouse Code', key: 'destinationWarehouseCode' },
    { name: 'Destination Warehouse Name', key: 'destinationWarehouseName' },
    { name: 'Facility State', key: null },
    { name: 'Facility LGA', key: null },
    { name: 'Generic product Code', key: 'genericProductCode' },
    { name: 'Product ID', key: 'productId' },
    { name: 'Product Name', key: 'productName' },
    { name: 'Product Code', key: 'productCode' },
    { name: 'Quantity Ordered', key: 'quantityOrdered' },
    { name: 'Quantity Packed', key: null },
    { name: 'Quantity Received', key: null },
    { name: 'Unit Volume', key: 'unitVolume' },
    { name: 'Unit Price', key: 'unitPrice' },
    { name: 'Funder', key: 'funderId' },
    { name: 'Program', key: 'programId' },
    { name: 'Carrier Name', key: null },
    { name: 'Driver Name', key: null },
    { name: 'Status', key: 'status' },
    { name: 'Creation Date', key: 'createdAt' },
    { name: 'Pickup Date', key: null },
    { name: 'Delivery Date', key: null }
  ]

  const headers = columns.map(col => col.name)
  const data = rows.map(row => columns.map(col => col.key ? get(row, col.key, '') : ''))

  return [headers].concat(data)
}

const generateExcelSheet = (aoa) => {
  return new Promise((resolve) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'long-haul-orders-export')
    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      bookSST: false,
      type: 'array'
    })
    resolve(new global.Blob([wbout], { type: 'application/octet-stream' }))
  })
}

async function exportLongHaulOrders (state, params) {
  const data = await exportLongHaulOrdersData(state, params)
  if (params.dryRun) return data
  return generateExcelSheet(data)
}

module.exports = { exportLongHaulOrders }
